import { FunctionComponent, useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import color from 'src/theme/color';
import { NavbarUpdated } from '../../../shared';
import CopyRightMessage from '../../../shared/CopyRightMessage';

import { Container } from '../../styled';
import {
    SetupCard,
    StyledButton,
    CompanyBox,
    TitleBox,
    TitleText,
    EmojiSpan,
    ButtonContentBox,
    ButtonTextBox,
    ButtonIconBox,
    NextButtonContainer,
    NextButton,
    ContentContainer
} from './styled';

import IntegrationServices from './IntegrationServices';
import ProjectDetails from './ProjectDetails';
import TeamConfiguration from './ProjectDetails/TeamConfiguration';
import Thankyou from './Thankyou';

import { getProjectDetailsFromState } from '../../../redux/onboarding/selectors';
import { TeamMember, ProjectSkill } from '../../../types/projects';
import useAppDispatch from '../../../hooks/useAppDispatch';
import { createProject } from '../../../redux/onboarding/service';
import Loader from '../../../shared/LoaderLinear';

type Props = Record<string, never>;

const Onboarding: FunctionComponent<Props> = ({}: Props) => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const companyName = (searchParams.get('company') || 'Beehive Software Inc')
        .split(/[\s_-]/)
        .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ');
    const [isStartHovered, setIsStartHovered] = useState(false);
    const [isNextHovered, setIsNextHovered] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const projectDetails = useSelector(getProjectDetailsFromState);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [skills, setSkills] = useState<ProjectSkill[]>([]);

    const handleSetup = async () => {
        try {
            setIsLoading(true);
            await dispatch(
                createProject({
                    code: location.hash.slice(1),
                    projectName: companyName
                })
            );
            setCurrentStep(1);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (projectDetails.details.projectId && currentStep === 0) {
            setCurrentStep(1);
        }
    }, [projectDetails, currentStep]);

    const renderStepContent = () => {
        if (currentStep === 0) {
            return (
                <SetupCard>
                    {isLoading && <Loader />}
                    <TitleBox>
                        <Typography
                            variant="body1"
                            color="white"
                            component={TitleText}
                        >
                            Set up your project info{' '}
                            <EmojiSpan role="img" aria-label="folder">
                                🍯
                            </EmojiSpan>
                        </Typography>
                    </TitleBox>
                    <ButtonContentBox>
                        <StyledButton
                            variant="contained"
                            fullWidth
                            onClick={() => handleSetup()}
                            onMouseEnter={() => setIsStartHovered(true)}
                            onMouseLeave={() => setIsStartHovered(false)}
                            disabled={isLoading}
                        >
                            <ButtonContentBox>
                                <ButtonTextBox $isHovered={isStartHovered}>
                                    {isLoading ? 'Setting up...' : 'Start'}
                                </ButtonTextBox>
                                <ButtonIconBox $isHovered={isStartHovered}>
                                    🐝
                                </ButtonIconBox>
                            </ButtonContentBox>
                        </StyledButton>
                    </ButtonContentBox>
                </SetupCard>
            );
        } else if (currentStep === 1) {
            return (
                <ContentContainer>
                    <IntegrationServices />
                    <NextButtonContainer>
                        <NextButton
                            variant="contained"
                            onClick={() => setCurrentStep(2)}
                            onMouseEnter={() => setIsNextHovered(true)}
                            onMouseLeave={() => setIsNextHovered(false)}
                        >
                            <ButtonContentBox>
                                <ButtonTextBox $isHovered={isNextHovered}>
                                    Next
                                </ButtonTextBox>
                                <ButtonIconBox $isHovered={isNextHovered}>
                                    🐝
                                </ButtonIconBox>
                            </ButtonContentBox>
                        </NextButton>
                    </NextButtonContainer>
                </ContentContainer>
            );
        } else if (currentStep === 2) {
            return (
                <ProjectDetails
                    setCurrentStep={setCurrentStep}
                    projectId={projectDetails.details.projectId}
                    skills={skills}
                    setSkills={setSkills}
                    teamMembers={teamMembers}
                    setTeamMembers={setTeamMembers}
                />
            );
        } else if (currentStep === 3) {
            return (
                <TeamConfiguration
                    setCurrentStep={setCurrentStep}
                    teamMembers={teamMembers}
                    setTeamMembers={setTeamMembers}
                    projectId={projectDetails.details.projectId}
                />
            );
        } else {
            return (
                <Thankyou projectName={projectDetails.details.projectName} />
            );
        }
    };

    return (
        <NavbarUpdated>
            <Container>
                {currentStep === 0 && (
                    <CompanyBox>
                        <Typography variant="h6" color={color.white}>
                            {companyName}
                        </Typography>
                    </CompanyBox>
                )}
                {renderStepContent()}
                <CopyRightMessage />
            </Container>
        </NavbarUpdated>
    );
};

export default Onboarding;
